import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mayGetAlertDigest } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { Notice } from '../ui/Badge';
import {
  StaleNotice,
  UnresolvedState,
  awaitingServer,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { Toggle } from '../ui/Toggle';
import { usePendingOverride } from '../ui/usePendingOverride';
import { IconAlerts } from '../icons';
import { useSession } from '../session';

/**
 * "Morning alerts email" (ops insights Phase 7 spec §8): the signed-in person's OWN switch for
 * the 7 AM email of what needs someone on the Alerts board. On until they turn it off.
 *
 * Only for a role that gets the email (`mayGetAlertDigest`: the owner and the office): for any
 * other role it draws nothing and asks the server nothing (the server refuses them too). The switch shows the server's answer, with a
 * flip laid over it until the server answers (`usePendingOverride`); a refused save drops the
 * flip and says why.
 *
 * While this server cannot send email (MAP 249), the switch still saves, and says plainly that
 * nothing goes out yet.
 */
export function MorningEmailSwitch() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const may = mayGetAlertDigest(useSession().role);
  const q = useQuery({ ...trpc.userPrefs.alertDigest.queryOptions(), enabled: may });
  const [error, setError] = useState<string | null>(null);
  const override = usePendingOverride<{ digest: boolean }>();
  const reread = () =>
    void qc.invalidateQueries({ queryKey: trpc.userPrefs.alertDigest.queryKey() });
  const save = useMutation(
    trpc.userPrefs.setAlertDigest.mutationOptions({
      // Never await a read inside a write's handler (AutomationsCard's note on `save`).
      onSuccess: () => {
        setError(null);
        reread();
      },
      onError: (e) => {
        setError(e.message);
        override.clear();
        reread();
      },
    }),
  );

  if (!may) return null;
  const state = shownState(q, q.data);
  if (!q.data) {
    return (
      <div className="mt-3 border-t border-border pt-3">
        <UnresolvedState
          state={unresolved(state)}
          what="your morning email setting"
          icon={IconAlerts}
          error={q.error}
          onRetry={() => void q.refetch()}
          skeleton="h-11"
        />
      </div>
    );
  }

  const busy = awaitingServer(save);
  const shown = override.apply({ digest: q.data.digest }, q.dataUpdatedAt, busy);
  return (
    <div className="mt-3 border-t border-border pt-3" data-morning-email>
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Toggle
        label="Morning alerts email"
        on={shown.digest}
        disabled={busy}
        focusableWhenDisabled
        onChange={(on) => {
          override.record({ digest: on }, q.dataUpdatedAt);
          save.mutate({ digest: on });
        }}
      />
      <p className="text-xs text-muted-foreground">
        At 7 AM shop time: open alerts and recorded automatic price raises. No email when
        there are no open alerts and no automatic raises.
      </p>
      {q.data.emailConfigured ? null : (
        <Notice tone="warn">
          Email isn&apos;t connected yet. The morning email will start when it is.
        </Notice>
      )}
    </div>
  );
}
